import { defaultAddress } from '@getcaya/scan-center'
import type { TableColumnProps } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import type {
  Source,
  SourceAddressFragment as SourceAddress,
  SourceStage,
} from '~app/graph/generated/graphql'
// Column components
import Address from './columns/Address'
import Duration from './columns/Duration'
import ExternalId from './columns/ExternalId'
import ScanCenter from './columns/ScanCenter'
import Stage from './columns/Stage'
import StartDate from './columns/StartDate'
import StageFilters from './StageFilters'

/* Column config */
interface ColumnPropsEditable<T> extends TableColumnProps<T> {
  editable?: boolean
}

const ListEntry = styled.p`
  margin: 0;
`

const getUnixTimestamp = (timestamp: string) => {
  return new Date(timestamp).getTime() / 1000
}

const createAddress = (address: SourceAddress) => ({
  business: false,
  ...defaultAddress,
  ...address,
})

type UseSourcesColumnConfig = (c: {
  stages?: SourceStage[]
  onActionDone: VoidFunction
}) => ColumnPropsEditable<Source>[]

const useSourcesColumnConfig: UseSourcesColumnConfig = ({
  stages,
  onActionDone,
}) => {
  const { t } = useTranslation()
  return [
    {
      key: 'id',
      title: 'ID',
      render: (_, record) => <small>{record.id} </small>,
    },
    {
      key: 'extId',
      title: 'External ID',
      editable: true,
      render: (_, { id, externalId }) => (
        <ExternalId sourceId={id} externalId={externalId} />
      ),
    },
    {
      key: 'scancenter',
      title: 'Scan Center',
      editable: true,
      render: (_, { id, scanCenter }: Source) => (
        <ScanCenter sourceId={id} scanCenterCode={scanCenter} />
      ),
    },
    {
      key: 'type',
      title: t('sources.type'),
      render: (_, { metadata }) => (metadata.business ? 'Business' : 'Private'),
    },
    {
      key: 'publicId',
      title: t('documentView.publicId'),
      render: (_, { customer }) => customer.encodedPublicId,
    },
    {
      key: 'customer',
      title: 'Customer',
      render: (_, { customer }) => {
        const name =
          customer.companyName || `${customer.firstName} ${customer.lastName}`
        return (
          <div>
            <ListEntry style={{ margin: 0 }}>{name} </ListEntry>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={
                'https://amn-ds.chargebee.com/customers?view_code=all&Customers.search=' +
                customer.id
              }
            >
              <i>{customer.id} </i>
            </a>
          </div>
        )
      },
    },
    {
      key: 'forwardingNames',
      title: 'Forwarding Names',
      render: (_, { address }) => {
        const { lines } = address
        return lines.map(line => (
          <ListEntry key={line.replaceAll(' ', '-')}> {line} </ListEntry>
        ))
      },
    },
    {
      key: 'address',
      title: t('misc.address'),
      editable: true,
      render: (_, { id, address, metadata }: Source) => {
        return (
          <Address
            sourceId={id}
            isBusiness={!!metadata.business}
            address={createAddress(address)}
          />
        )
      },
    },
    {
      key: 'duration',
      title: t('sources.duration'),
      editable: true,
      render: (_, { id, duration }: Source) => (
        <Duration sourceId={id} duration={duration} />
      ),
    },
    {
      key: 'startAt',
      title: t('sources.startAt'),
      editable: true,
      render: (_, record: Source) => (
        <StartDate
          sourceId={record.id}
          startDate={record.startAt as string | undefined}
        />
      ),

      defaultSortOrder: 'descend' as const,
      sorter: (a: Source, b: Source) =>
        getUnixTimestamp(a.startAt) - getUnixTimestamp(b.startAt),
    },
    {
      key: 'endAt',
      title: t('sources.endAt'),
      render: (_, { endAt }) => endAt && dayjs(endAt).format('DD.MM.YYYY'),
      defaultSortOrder: 'descend' as const,
      sorter: (a, b) => getUnixTimestamp(a.endAt) - getUnixTimestamp(b.endAt),
    },
    {
      key: 'actions',
      title: t('misc.action'),
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <StageFilters
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys as SourceStage[]}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),

      filteredValue: stages || undefined,
      sorter: (a, b) => a.stage.localeCompare(b.stage),
      defaultSortOrder: 'descend' as const,
      filterMultiple: true,
      render: (_, { id, stage, startAt }: Source) => {
        return (
          <Stage
            sourceId={id}
            stage={stage}
            startAt={startAt}
            onDone={onActionDone}
          />
        )
      },
    },
  ]
}
export default useSourcesColumnConfig
